import React, { useState } from "react";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetCmsQuery } from "../../services/auth";
import { PageHero, LoadingState, ErrorState } from "./PublicShared";
import "./PublicPages.scss";

export default function Support() {
  const { data: cmsData, isLoading, isError, refetch } = useGetCmsQuery();

  const cms = cmsData?.data;
  const contactEmail = cms?.contact_email || "support@itsmywall.com";
  const countryCode = cms?.country_code || "+1";
  const contactNumber = cms?.contact_number || "8005551234";
  const displayPhone = `${countryCode} ${contactNumber}`.trim();
  const rawPhone = `${countryCode}${contactNumber}`.replace(/[^\d+]/g, "");

  // Interactive contact form state
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formValues.name.trim()) nextErrors.name = "Please enter your name";
    if (!formValues.email.trim()) {
      nextErrors.email = "Please enter your email address";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
      nextErrors.email = "Please enter a valid email address";
    }
    if (!formValues.subject.trim()) nextErrors.subject = "Please enter a subject";
    if (!formValues.message.trim()) nextErrors.message = "Please enter your message";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Compose mailto link so user's client opens with their message directed to support
    const subjectParam = encodeURIComponent(`[Support Inquiry] ${formValues.subject}`);
    const bodyParam = encodeURIComponent(
      `Name: ${formValues.name}\nEmail: ${formValues.email}\n\nMessage:\n${formValues.message}`
    );

    window.location.href = `mailto:${contactEmail}?subject=${subjectParam}&body=${bodyParam}`;

    toast.success("Thank you! Opening your email client to send your message to support.");
    setIsSubmitted(true);
    setFormValues({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div className="public_page_container">
      <PageHero
        badge="Customer Support"
        badgeIcon={<SupportAgentOutlinedIcon />}
        title="Support & Help Desk"
        subtitle="We're here to help you get the most out of It's My Wall. Reach out to our team anytime."
      />

      <div className="public_card_container">
        {isLoading ? (
          <LoadingState lines={7} />
        ) : isError ? (
          <ErrorState
            title="Unable to load contact information"
            message="We were unable to load the latest support details from the server. Please try again."
            onRetry={refetch}
          />
        ) : (
          <>
            {/* Contact Method Cards Grid */}
            <div className="support_cards_grid">
              {/* Phone Card */}
              <div className="support_contact_card">
                <div className="card_top">
                  <div className="card_icon">
                    <PhoneOutlinedIcon />
                  </div>
                  <div className="card_header">
                    <h3>Direct Phone</h3>
                    <p>Mon - Fri, 9am - 6pm EST</p>
                  </div>
                </div>

                <div className="card_value">{displayPhone}</div>

                <a href={`tel:${rawPhone}`} className="card_action_btn">
                  <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                  <span>Call Us Now</span>
                </a>
              </div>

              {/* Email Card */}
              <div className="support_contact_card">
                <div className="card_top">
                  <div className="card_icon">
                    <EmailOutlinedIcon />
                  </div>
                  <div className="card_header">
                    <h3>Email Support</h3>
                    <p>Expect response within 24h</p>
                  </div>
                </div>

                <div className="card_value">{contactEmail}</div>

                <a
                  href={`mailto:${contactEmail}?subject=Support%20Inquiry`}
                  className="card_action_btn"
                >
                  <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                  <span>Send an Email</span>
                </a>
              </div>

              {/* FAQ Quick Link Card */}
              <div className="support_contact_card">
                <div className="card_top">
                  <div className="card_icon">
                    <HelpOutlineOutlinedIcon />
                  </div>
                  <div className="card_header">
                    <h3>Knowledge Base</h3>
                    <p>Instant answers to common topics</p>
                  </div>
                </div>

                <div className="card_value">Frequently Asked Questions</div>

                <Link to="/public/faq" className="card_action_btn">
                  <HelpOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  <span>Browse FAQs</span>
                </Link>
              </div>
            </div>

            {/* Inquiry Contact Form */}
            <div className="support_inquiry_section">
              <div className="section_header">
                <h3>Send Us a Message</h3>
                <p>
                  Have a specific question, bug report, or enterprise inquiry? Fill in the details below.
                </p>
              </div>

              {isSubmitted && (
                <div
                  style={{
                    background: "rgba(140, 227, 82, 0.1)",
                    border: "1px solid rgba(140, 227, 82, 0.3)",
                    borderRadius: 12,
                    padding: "16px 20px",
                    marginBottom: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    color: "#AFF287",
                  }}
                >
                  <CheckCircleOutlineIcon />
                  <span>
                    Your inquiry has been routed to <strong>{contactEmail}</strong>. Our team will review and reply within 24 business hours.
                  </span>
                </div>
              )}

              <form className="inquiry_form" onSubmit={handleSubmit} noValidate>
                <div className="form_group">
                  <label htmlFor="support-name">Your Full Name *</label>
                  <input
                    id="support-name"
                    type="text"
                    name="name"
                    placeholder="e.g. Alex Morgan"
                    value={formValues.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <span className="field_error">{errors.name}</span>}
                </div>

                <div className="form_group">
                  <label htmlFor="support-email">Email Address *</label>
                  <input
                    id="support-email"
                    type="email"
                    name="email"
                    placeholder="e.g. alex@example.com"
                    value={formValues.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="field_error">{errors.email}</span>}
                </div>

                <div className="form_group full_width">
                  <label htmlFor="support-subject">Subject *</label>
                  <input
                    id="support-subject"
                    type="text"
                    name="subject"
                    placeholder="e.g. Question about SVG design export resolution"
                    value={formValues.subject}
                    onChange={handleInputChange}
                  />
                  {errors.subject && <span className="field_error">{errors.subject}</span>}
                </div>

                <div className="form_group full_width">
                  <label htmlFor="support-message">Message *</label>
                  <textarea
                    id="support-message"
                    name="message"
                    placeholder="Describe your inquiry in detail..."
                    value={formValues.message}
                    onChange={handleInputChange}
                    rows={4}
                  />
                  {errors.message && <span className="field_error">{errors.message}</span>}
                </div>

                <div className="form_actions">
                  <button type="submit" className="btn_submit">
                    <SendOutlinedIcon sx={{ fontSize: 16 }} />
                    <span>Send Message</span>
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
